import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import { WeekRangeFilter } from '../ui/week-filter';
import i18n from '../../i18n';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { 
  Users, 
  DollarSign, 
  Clock, 
  CheckCircle2, 
  ChevronDown, 
  ChevronUp,
  RefreshCw,
  FileText,
  AlertCircle,
  Download,
  Calendar,
  Check,
  Banknote,
  X,
} from 'lucide-react';
import { CopyNumberButton } from '../shared/CopyNumberButton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Format currency with PLN
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'PLN'
  }).format(amount || 0);
};

const Money = ({ v, testId }) => (
  <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
    {formatCurrency(v)}<CopyNumberButton value={v || 0} size="sm" testId={testId} />
  </span>
);

/**
 * Format date to YYYY.MM.DD
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toISOString().split('T')[0].replace(/-/g, '.');
};

/**
 * Status badge for expenses
 */
const getStatusBadge = (status) => {
  const statusConfig = {
    draft: { color: 'bg-gray-500', label: i18n.t('budget.draft') },
    submitted: { color: 'bg-blue-500', label: i18n.t('budget.submitted') },
    manager_approved: { color: 'bg-yellow-500', label: i18n.t('budget.managerApproved') },
    admin_approved: { color: 'bg-green-500', label: i18n.t('budget.adminApproved') },
    rejected: { color: 'bg-red-500', label: i18n.t('budget.rejected') },
    paid: { color: 'bg-purple-500', label: i18n.t('budget.paid') }
  };
  const config = statusConfig[status] || { color: 'bg-gray-500', label: status };
  return <Badge className={`${config.color} text-white text-xs`}>{config.label}</Badge>;
};

/**
 * Employee Reimbursement Card
 */
const EmployeeReimbursementCard = ({ employee, onMarkPaid, onMarkSinglePaid, onExportExcel, onExportPdf, exporting }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const pendingExpenses = employee.expenses?.filter(
    e => ['submitted', 'manager_approved', 'admin_approved'].includes(e.status)
  ) || [];

  // "Approved" = ready to pay via a batch. We highlight the count +
  // total PLN so admins can eyeball how much cash the next payment run
  // will move without opening the batch dialog.
  const approvedExpenses = employee.expenses?.filter(e => e.status === 'admin_approved') || [];
  const approvedCount = approvedExpenses.length;
  const approvedAmountPln = approvedExpenses.reduce(
    (acc, e) => acc + (e.amount_pln || e.amount || 0),
    0,
  );

  return (
    <Card className="mb-4" data-testid={`reimbursement-card-${employee.employee_id}`}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
              <div className="flex min-w-0 items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{employee.employee_name}</CardTitle>
                  <CardDescription className="text-sm">
                    {employee.employee_department && `${employee.employee_department} • `}
                    {employee.employee_position || t('budget.employeeFallback')}
                  </CardDescription>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-2 ml-auto">
                {/* Per-employee export buttons — stop-propagation so the
                    click doesn't collapse/expand the card. */}
                {onExportExcel && (
                  <div className="flex items-center gap-1">
                    <DocLangToggle testId={`doc-lang-reimbursementdashboard-1-${employee.employee_id}`} />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8"
                      disabled={exporting}
                      onClick={(e) => { e.stopPropagation(); onExportExcel(); }}
                      title={t('budget.exportThisEmployeeSReimbursementReport')}
                      data-testid={`export-employee-excel-${employee.employee_id}`}
                    >
                      <Download className="h-3.5 w-3.5 mr-1" /> {t('budget.xlsx')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8"
                      disabled={exporting}
                      onClick={(e) => { e.stopPropagation(); onExportPdf(); }}
                      title={t('budget.exportThisEmployeeSReimbursementReport2')}
                      data-testid={`export-employee-pdf-${employee.employee_id}`}
                    >
                      <Download className="h-3.5 w-3.5 mr-1" /> {t('budget.pdf')}
                    </Button>
                  </div>
                )}
                {/* Pending Amount - Highlight */}
                <div className="text-right">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">{t('budget.pending')}</div>
                  <div className={`text-lg font-bold ${employee.pending_amount_pln > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                    <Money v={employee.pending_amount_pln} testId={`copy-reimb-pending-${employee.employee_id}`} />
                  </div>
                  <div className="text-xs text-gray-500">{t('budget.expenseCount', { count: employee.pending_count })}</div>
                </div>

                {/* Ready to Pay (admin_approved) — feeds the batch payment file */}
                <div
                  className="text-right"
                  data-testid={`reimbursement-approved-summary-${employee.employee_id}`}
                >
                  <div className="text-xs text-emerald-700 uppercase tracking-wide">{t('budget.readyToPay')}</div>
                  <div className={`text-lg font-bold ${approvedCount > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                    <Money v={approvedAmountPln} testId={`copy-reimb-ready-${employee.employee_id}`} />
                  </div>
                  <div className="text-xs text-gray-500">{t('budget.expenseCount', { count: approvedCount })}</div>
                </div>

                {/* Paid Amount */}
                <div className="text-right">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">{t('budget.paid')}</div>
                  <div className="text-lg font-bold text-green-600">
                    <Money v={employee.paid_amount_pln} testId={`copy-reimb-paid-${employee.employee_id}`} />
                  </div>
                  <div className="text-xs text-gray-500">{t('budget.expenseCount', { count: employee.paid_count })}</div>
                </div>
                
                {/* Toggle icon */}
                <div className="text-gray-400">
                  {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            {/* Stats row */}
            <div className="grid grid-cols-5 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="text-xs text-gray-500">{t('budget.draft')}</div>
                <div className="font-semibold">{employee.draft_count}</div>
                <div className="text-xs text-gray-400"><Money v={employee.draft_amount_pln} testId={`copy-reimb-stat-draft-${employee.employee_id}`} /></div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500">{t('budget.pending')}</div>
                <div className="font-semibold text-orange-600">{employee.pending_count}</div>
                <div className="text-xs text-orange-400"><Money v={employee.pending_amount_pln} testId={`copy-reimb-stat-pending-${employee.employee_id}`} /></div>
              </div>
              <div className="text-center">
                <div className="text-xs text-emerald-700">{t('budget.readyToPay')}</div>
                <div className="font-semibold text-emerald-600">{approvedCount}</div>
                <div className="text-xs text-emerald-500"><Money v={approvedAmountPln} testId={`copy-reimb-stat-ready-${employee.employee_id}`} /></div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500">{t('budget.paid')}</div>
                <div className="font-semibold text-green-600">{employee.paid_count}</div>
                <div className="text-xs text-green-400"><Money v={employee.paid_amount_pln} testId={`copy-reimb-stat-paid-${employee.employee_id}`} /></div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500">{t('budget.rejected')}</div>
                <div className="font-semibold text-red-600">{employee.rejected_count}</div>
              </div>
            </div>
            
            {/* Pending Expenses List */}
            {pendingExpenses.length > 0 ? (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Clock className="h-4 w-4" /> {t('budget.pendingReimbursement')}
                </h4>
                <div className="divide-y border rounded-lg">
                  {pendingExpenses.map((expense, idx) => (
                    <div key={idx} className="p-3 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{expense.subcategory || expense.category}</span>
                          {getStatusBadge(expense.status)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {expense.project_name} • {formatDate(expense.expense_date)}
                          {expense.created_at && ` • ${t('budget.createdOn', { date: formatDate(expense.created_at) })}`}
                        </div>
                        {expense.description && (
                          <div className="text-xs text-gray-600 mt-1 break-words whitespace-pre-wrap" data-testid={`reimb-desc-${expense.id || idx}`}>
                            {expense.description}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="font-semibold">
                            <Money v={expense.amount_pln || expense.amount} testId={`copy-reimb-expense-${expense.id || idx}`} />
                          </div>
                          {expense.currency !== 'PLN' && expense.amount && (
                            <div className="text-xs text-gray-500">
                              ({expense.amount.toFixed(2)} {expense.currency})
                            </div>
                          )}
                        </div>
                        {/* Individual Mark as Paid button - only for admin_approved */}
                        {expense.status === 'admin_approved' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onMarkSinglePaid(expense);
                            }}
                            className="h-8 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                            title={t('budget.markAsPaid')}
                            data-testid={`mark-paid-single-${expense.id}`}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Mark all as paid button */}
                {employee.pending_count > 0 && (
                  <div className="flex justify-end mt-3">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onMarkPaid(employee.employee_id, pendingExpenses)}
                      data-testid={`mark-paid-btn-${employee.employee_id}`}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Mark All as Paid ({pendingExpenses.filter(e => e.status === 'admin_approved').length})
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <p className="text-sm">{t('budget.noPendingReimbursements')}</p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

/**
 * Reimbursement Dashboard Component
 */
const ReimbursementDashboard = () => {
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Filter state
  const currentYear = new Date().getFullYear();
  const [dateFrom, setDateFrom] = useState(`${currentYear}-01-01`);
  const [dateTo, setDateTo] = useState(`${currentYear}-12-31`);
  const [statusFilter, setStatusFilter] = useState('all');
  // Only "Own Payment" expenses are reimbursable — default the filter
  // to that but let admins expand to all methods if needed.
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('Own Payment');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // ── Batch payment (SEPA / Elixir) modal state ──
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchCurrencyMode, setBatchCurrencyMode] = useState('original');
  const [batchExecutionDate, setBatchExecutionDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [batchMarkPaid, setBatchMarkPaid] = useState(false);
  // Employees ticked into this batch. `null` = "everyone" (default when
  // the modal opens); an explicit Set of ids = user-picked subset.
  const [batchEmployeeIds, setBatchEmployeeIds] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Mirror the export endpoint: send the same filters so the
      // on-screen totals always match what a "Export Excel/PDF" produces.
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (paymentMethodFilter) params.append('payment_method', paymentMethodFilter);
      if (searchQuery) params.append('q', searchQuery);
      const qs = params.toString();
      const response = await axios.get(`${API}/expenses/reimbursement-dashboard${qs ? '?' + qs : ''}`);
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error fetching reimbursement dashboard:', err);
      setError(err.response?.data?.detail || 'Failed to load reimbursement data');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, statusFilter, paymentMethodFilter, searchQuery]);

  // Debounce filter changes to avoid a fetch on every keystroke in
  // the search box, but still keep the UI responsive for selects.
  useEffect(() => {
    const t = setTimeout(fetchDashboard, 300);
    return () => clearTimeout(t);
  }, [fetchDashboard]);

  const handleMarkAllPaid = async (employeeId, expenses) => {
    const approvedExpenses = expenses.filter(e => e.status === 'admin_approved');
    
    if (approvedExpenses.length === 0) {
      toast.warning(t('budget.onlyAdminApprovedExpensesCanBe'));
      return;
    }
    
    if (!window.confirm(t('budget.confirmMarkPaidEmployee', { count: approvedExpenses.length }))) {
      return;
    }
    
    let successCount = 0;
    let failCount = 0;
    
    for (const expense of approvedExpenses) {
      try {
        await axios.post(`${API}/expenses/${expense.id}/change-status`, {
          action: 'mark_paid',
          comment: 'Bulk payment via Reimbursement Dashboard'
        });
        successCount++;
      } catch (err) {
        console.error(`Failed to mark expense ${expense.id} as paid:`, err);
        failCount++;
      }
    }
    
    if (failCount > 0) {
      toast.warning(`${successCount} marked as paid, ${failCount} failed`);
    } else {
      toast.success(t('budget.markedPaidCount', { count: successCount }));
    }
    
    // Refresh dashboard
    fetchDashboard();
  };

  // Handler for marking a single expense as paid
  const handleMarkSinglePaid = async (expense) => {
    if (expense.status !== 'admin_approved') {
      toast.warning(t('budget.onlyAdminApprovedExpensesCanBe'));
      return;
    }
    
    try {
      await axios.post(`${API}/expenses/${expense.id}/change-status`, {
        action: 'mark_paid',
        comment: 'Marked as paid via Reimbursement Dashboard'
      });
      toast.success(t('budget.expenseMarkedAsPaid'));
      fetchDashboard();
    } catch (err) {
      console.error(`Failed to mark expense ${expense.id} as paid:`, err);
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  // Generic exporter used for bulk + per-employee, Excel + PDF.
  const runExport = async ({ format = 'excel', employeeId = null } = {}) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (paymentMethodFilter && paymentMethodFilter !== 'all') params.append('payment_method', paymentMethodFilter);
      if (searchQuery) params.append('q', searchQuery);
      params.append('format', format);
      if (employeeId) params.append('employee_id', employeeId);

      const response = await axios.get(`${API}/expenses/reimbursement-export?${params.toString()}`, { responseType: 'blob' });
      const contentDisposition = response.headers['content-disposition'];
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      let filename = `reimbursements_${new Date().toISOString().split('T')[0]}.${ext}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) filename = filenameMatch[1].replace(/"/g, '');
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', weekFileName(filename));
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported: ${filename}`);
    } catch (err) {
      console.error('Error exporting reimbursements:', err);
      toast.error(err.response?.data?.detail || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  // Legacy export button preserved for backward compat.
  const handleExportExcel = () => runExport({ format: 'excel' });

  // Eligible employees for a batch: those with at least one
  // `admin_approved` reimbursable expense. Computed from the current
  // dashboard payload so it stays in sync with the visible filters.
  const batchEligibleEmployees = useMemo(() => {
    const src = dashboardData?.employees || [];
    return src
      .map((e) => ({
        ...e,
        approved_count: (e.expenses || []).filter((x) => x.status === 'admin_approved').length,
        approved_amount: (e.expenses || [])
          .filter((x) => x.status === 'admin_approved')
          .reduce((acc, x) => acc + (x.amount_pln || x.amount || 0), 0),
      }))
      .filter((e) => e.approved_count > 0);
  }, [dashboardData]);

  // Whenever the modal opens, default the tick set to "everyone eligible"
  // so admins can just hit Generate without extra clicks.
  useEffect(() => {
    if (batchOpen && batchEmployeeIds === null) {
      setBatchEmployeeIds(new Set(batchEligibleEmployees.map((e) => e.employee_id)));
    }
    if (!batchOpen) {
      // Reset selection so it re-syncs to whoever is eligible next time.
      setBatchEmployeeIds(null);
    }
  }, [batchOpen, batchEligibleEmployees, batchEmployeeIds]);

  const toggleBatchEmployee = (id) => {
    setBatchEmployeeIds((prev) => {
      const next = new Set(prev || batchEligibleEmployees.map((e) => e.employee_id));
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const batchAllChecked = (batchEmployeeIds?.size || 0) === batchEligibleEmployees.length && batchEligibleEmployees.length > 0;

  // Generate a SEPA XML + Elixir-0 batch payment file. See the modal
  // below for the currency/date/mark-as-paid options.
  const runBatchPayment = async () => {
    setBatchGenerating(true);
    try {
      const selected = Array.from(batchEmployeeIds || []);
      if (selected.length === 0) {
        toast.error(t('budget.pickAtLeastOneEmployeeTo'));
        setBatchGenerating(false);
        return;
      }
      const body = {
        execution_date: batchExecutionDate,
        currency_mode: batchCurrencyMode,
        mark_as_paid: batchMarkPaid,
        // Only pass the id list when the user has DE-selected somebody;
        // if every eligible employee is ticked, let the backend pick up
        // everyone (equivalent, but keeps request bodies small).
        ...(selected.length === batchEligibleEmployees.length ? {} : { employee_ids: selected }),
      };
      const resp = await axios.post(
        `${API}/expenses/reimbursement-batch-payment`,
        body,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/zip' }));
      const disposition = resp.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)/i);
      const fname = match ? match[1] : `reimbursement_batch_${batchExecutionDate}.zip`;
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', weekFileName(fname));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      const included = resp.headers['x-batch-included'];
      const skipped = resp.headers['x-batch-skipped'];
      toast.success(`Batch generated: ${included} paid, ${skipped} skipped`);
      if (batchMarkPaid) {
        fetchDashboard();
      }
      setBatchOpen(false);
    } catch (err) {
      // Blob-typed errors need to be text-decoded to surface the JSON detail.
      let msg = err.response?.data?.detail || 'Failed to generate batch payment file';
      if (err.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          const j = JSON.parse(txt);
          msg = j.detail || msg;
        } catch (_) {}
      }
      console.error('Batch payment error:', err);
      toast.error(msg);
    } finally {
      setBatchGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('budget.loadingReimbursementData')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-700">{error}</p>
          <Button onClick={fetchDashboard} variant="outline" className="mt-4" data-testid="btn-fetchdashboard">
            <RefreshCw className="h-4 w-4 mr-2" /> {t('budget.retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { employees, summary } = dashboardData || { employees: [], summary: {} };

  // Aggregate the "ready-to-pay" (admin_approved) totals across every
  // visible employee so the top summary card mirrors the per-card badges.
  const totalReadyAmount = (employees || []).reduce(
    (acc, emp) => acc + (emp.expenses || [])
      .filter((x) => x.status === 'admin_approved')
      .reduce((a, x) => a + (x.amount_pln || x.amount || 0), 0),
    0,
  );
  const totalReadyCount = (employees || []).reduce(
    (acc, emp) => acc + (emp.expenses || []).filter((x) => x.status === 'admin_approved').length,
    0,
  );

  return (
    <div className="space-y-6" data-testid="reimbursement-dashboard">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('budget.totalPendingReimbursement')}</p>
                <p className="text-2xl font-bold text-orange-600" data-testid="total-pending-amount">
                  <Money v={summary.total_pending_amount_pln} testId="copy-total-pending" />
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Banknote className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-emerald-700">{t('budget.readyToPayBatch')}</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="total-ready-amount">
                  <Money v={totalReadyAmount} testId="copy-total-ready" />
                </p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  {t('budget.expenseCount', { count: totalReadyCount })} · {t('budget.adminApproved')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('budget.totalPaid')}</p>
                <p className="text-2xl font-bold text-green-600" data-testid="total-paid-amount">
                  <Money v={summary.total_paid_amount_pln} testId="copy-total-paid" />
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('budget.employeesWithExpenses')}</p>
                <p className="text-2xl font-bold text-blue-600" data-testid="employee-count">
                  {summary.employee_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Export Controls */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">{t('budget.filter')}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('budget.from')}</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-8 w-44"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('budget.to')}</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-8 w-44"
              />
            </div>
            <WeekRangeFilter from={dateFrom} to={dateTo} onChange={(f, t2) => { setDateFrom(f); setDateTo(t2); }} testId="reimb-week-filter" />
            
            <div className="h-6 w-px bg-gray-300" />
            
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('budget.status2')}</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 w-40">
                  <SelectValue placeholder={t('budget.allStatuses2')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('budget.allStatuses')}</SelectItem>
                  <SelectItem value="pending">{t('budget.pending')}</SelectItem>
                  <SelectItem value="admin_approved">{t('budget.readyToPay2')}</SelectItem>
                  <SelectItem value="paid">{t('budget.paid')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="h-6 w-px bg-gray-300" />

            {/* Payment method — default is `Own Payment` because only
                employee-paid expenses are reimbursable. */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('budget.method')}</Label>
              <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
                <SelectTrigger className="h-8 w-40" data-testid="reimb-method-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Own Payment">{t('budget.ownPayment')}</SelectItem>
                  <SelectItem value="Company Card">{t('budget.companyCard')}</SelectItem>
                  <SelectItem value="Bank Transfer">{t('budget.bankTransfer')}</SelectItem>
                  <SelectItem value="all">{t('budget.allMethods')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="h-6 w-px bg-gray-300" />

            {/* Employee filter — pulled from the dashboard payload so it
                mirrors exactly what's visible below. */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('budget.employee2')}</Label>
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger className="h-8 w-48" data-testid="reimb-employee-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('budget.allEmployees2')}</SelectItem>
                  {(dashboardData?.employees || []).map(e => (
                    <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="h-6 w-px bg-gray-300" />

            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('budget.search')}</Label>
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('budget.descriptionVendorInvoice')}
                className="h-8 w-56"
                data-testid="reimb-search-input"
              />
            </div>

            <div className="flex-grow" />

            {/* Clear filters — resets every filter above (dates,
                status, payment method, employee, search) to their
                initial values so the accountant doesn't have to
                click each dropdown back to "all" by hand. */}
            {(
              dateFrom !== `${currentYear}-01-01` ||
              dateTo !== `${currentYear}-12-31` ||
              statusFilter !== 'all' ||
              paymentMethodFilter !== 'Own Payment' ||
              employeeFilter !== 'all' ||
              !!searchQuery
            ) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDateFrom(`${currentYear}-01-01`);
                  setDateTo(`${currentYear}-12-31`);
                  setStatusFilter('all');
                  setPaymentMethodFilter('Own Payment');
                  setEmployeeFilter('all');
                  setSearchQuery('');
                }}
                className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="reimb-clear-filters-btn"
                title={t('budget.resetEveryFilterDateRangeStatus')}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                {t('budget.clearFilters')}
              </Button>
            )}

            {/* Bulk export buttons */}
            <DocLangToggle testId="doc-lang-reimbursementdashboard-0" />
            <Button
              onClick={() => runExport({ format: 'excel' })}
              disabled={exporting}
              variant="outline"
              data-testid="export-reimbursements-btn"
            >
              {exporting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Excel
            </Button>
            <Button
              onClick={() => runExport({ format: 'pdf' })}
              disabled={exporting}
              variant="outline"
              data-testid="export-reimbursements-pdf-btn"
            >
              {exporting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              PDF
            </Button>
            <Button
              onClick={() => setBatchOpen(true)}
              variant="default"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="open-batch-payment-btn"
            >
              <Banknote className="h-4 w-4 mr-2" />
              {t('budget.batchPayment')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Refresh button */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">{t('budget.employeeReimbursements')}</h2>
        <Button variant="outline" size="sm" onClick={fetchDashboard} data-testid="btn-fetchdashboard">
          <RefreshCw className="h-4 w-4 mr-2" /> {t('budget.refresh')}
        </Button>
      </div>

      {/* Employee Cards */}
      {(() => {
        // Server-side filters (date, status, payment_method, q) are
        // already applied by /reimbursement-dashboard, so only the
        // employee scope needs to be honoured on the client.
        const filteredEmployees = employeeFilter === 'all'
          ? employees
          : employees.filter(e => e.employee_id === employeeFilter);
        if (filteredEmployees.length === 0) {
          return (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">{t('budget.noEmployeesMatchTheCurrentFilters')}</p>
              </CardContent>
            </Card>
          );
        }
        return (
          <div>
            {filteredEmployees.map((employee) => (
              <EmployeeReimbursementCard
                key={employee.employee_id}
                employee={employee}
                onMarkPaid={handleMarkAllPaid}
                onMarkSinglePaid={handleMarkSinglePaid}
                onExportExcel={() => runExport({ format: 'excel', employeeId: employee.employee_id })}
                onExportPdf={() => runExport({ format: 'pdf', employeeId: employee.employee_id })}
                exporting={exporting}
              />
            ))}
          </div>
        );
      })()}

      {/* Batch Payment modal (SEPA XML + Elixir-0) */}
      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent
          className="max-w-lg w-[calc(100vw-2rem)] p-0 max-h-[calc(100vh-2rem)] gap-0 flex flex-col overflow-hidden"
          data-testid="batch-payment-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-gray-100 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5 text-emerald-600" />
              {t('budget.generateBatchPaymentFile')}
            </DialogTitle>
            <DialogDescription>
              {t('budget.batchPaymentDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 px-6 py-4 overflow-y-auto flex-1 min-h-0">
            <div>
              <Label className="text-sm font-medium mb-2 block">{t('budget.paymentCurrency')}</Label>
              <RadioGroup value={batchCurrencyMode} onValueChange={setBatchCurrencyMode} className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <RadioGroupItem value="original" id="cur-original" className="mt-1" data-testid="cur-mode-original" />
                  <div>
                    <div className="text-sm font-medium">{t('budget.originalCurrency')}</div>
                    <div className="text-xs text-gray-500">
                      {t('budget.batch.originalDesc')}
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <RadioGroupItem value="pln" id="cur-pln" className="mt-1" data-testid="cur-mode-pln" />
                  <div>
                    <div className="text-sm font-medium">{t('budget.plnUsingRecordedConversionRate')}</div>
                    <div className="text-xs text-gray-500">
                      {t('budget.batch.plnDesc')}
                    </div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="batch-exec-date" className="text-sm font-medium mb-2 block">
                {t('budget.executionDate')}
              </Label>
              <Input
                id="batch-exec-date"
                type="date"
                value={batchExecutionDate}
                onChange={(e) => setBatchExecutionDate(e.target.value)}
                className="w-48"
                data-testid="batch-exec-date"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('budget.batch.execDateHelp')}
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">
                  {t('budget.batch.employeesToInclude')}{' '}
                  <span className="text-gray-500 font-normal">
                    ({t('budget.batch.ticked', { n: batchEmployeeIds?.size || 0, total: batchEligibleEmployees.length })})
                  </span>
                </Label>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:text-blue-700 underline"
                  onClick={() => setBatchEmployeeIds(
                    batchAllChecked
                      ? new Set()
                      : new Set(batchEligibleEmployees.map((e) => e.employee_id))
                  )}
                  data-testid="batch-toggle-all-employees"
                >
                  {batchAllChecked ? t('budget.deselectAll') : t('budget.selectAll')}
                </button>
              </div>
              {batchEligibleEmployees.length === 0 ? (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500">
                  {t('budget.batch.noneEligible')}
                </div>
              ) : (
                <div className="max-h-56 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100">
                  {batchEligibleEmployees.map((emp) => {
                    const ticked = batchEmployeeIds?.has(emp.employee_id) ?? true;
                    return (
                      <label
                        key={emp.employee_id}
                        className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Checkbox
                            checked={ticked}
                            onCheckedChange={() => toggleBatchEmployee(emp.employee_id)}
                            data-testid={`batch-employee-${emp.employee_id}`}
                          />
                          <span className="text-sm truncate">{emp.employee_name}</span>
                        </div>
                        <span className="text-xs text-gray-500 shrink-0 tabular-nums">
                          {t('budget.expenseCount', { count: emp.approved_count })} · {emp.approved_amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 p-3">
              <Checkbox
                id="batch-mark-paid"
                checked={batchMarkPaid}
                onCheckedChange={(v) => setBatchMarkPaid(!!v)}
                data-testid="batch-mark-paid"
              />
              <Label htmlFor="batch-mark-paid" className="text-sm leading-snug cursor-pointer">
                <span className="font-medium">{t('budget.markIncludedExpensesAsPaid')}</span>
                <span className="block text-xs text-gray-600">
                  {t('budget.batch.markPaidHelp')}
                </span>
              </Label>
            </div>
          </div>

          <DialogFooter className="px-6 py-3 border-t border-gray-100 bg-white shrink-0">
            <Button variant="outline" onClick={() => setBatchOpen(false)} disabled={batchGenerating} data-testid="dialog-cancel-btn">
              {t('budget.cancel')}
            </Button>
            <Button
              onClick={runBatchPayment}
              disabled={batchGenerating || !batchExecutionDate || !batchEmployeeIds || batchEmployeeIds.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="run-batch-payment-btn"
            >
              {batchGenerating ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> {t('budget.generating')}</>
              ) : (
                <><Download className="h-4 w-4 mr-2" /> {t('budget.batch.generate')}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReimbursementDashboard;
